import { dayjs } from '@mobi/utils'
import type { Tabs } from '@core/Areas/AccountActivity/types'
import type { BetLoopFeedParams } from '@core/Areas/BetLoop/helpers/api/types'

/** DO NOT EXPORT - Compiler will remove reference */
const enum LocalConstants {
  accountBase = 'account',
  accountActivityBase = 'account-activity',
  primaryAccountHolderDetailsBase = 'primary-account-holder-details',
  blackbookRunnersBase = 'blackbook-runners',
  racePageFormBase = 'race-page-form',
  racePageCompleteBase = 'race-page-complete',
  raceReplyBase = 'race-replay',
  braintreeBase = 'braintree',
  betLoopBase = 'bet-loop',
}

export const queryKeys = {
  accountBase: LocalConstants.accountBase,
  accountContactDetails: (accountNumber: number) =>
    `${LocalConstants.accountBase}-contact-${accountNumber}`,
  accountPreferences: (accountNumber: number) =>
    `${LocalConstants.accountBase}-preferences-${accountNumber}`,
  accountDetailsForMenu: [LocalConstants.accountBase, 'detailsForMenu'],
  accountActivityBase: LocalConstants.accountActivityBase,
  accountActivityData: (tab: Tabs, isOrderByEvent: boolean, userSelectedDate?: string) => [
    LocalConstants.accountBase,
    LocalConstants.accountActivityBase,
    tab,
    isOrderByEvent,
    userSelectedDate,
  ],

  primaryAccountHolderDetails: (accountNumber: number) =>
    `${LocalConstants.primaryAccountHolderDetailsBase}-${accountNumber}`,

  activityStatementsAccountDetails: 'account-details',
  activityStatementsAccountHolders: 'account-holders',

  banner: 'banner',
  raceCardPromotion: 'race-card-promotion',

  blackbookComments: 'blackbook-comments',
  blackbookDetails: 'blackbook-details',
  blackbookPushAlerts: 'blackbook-push-alerts',
  blackbookRunnersBase: LocalConstants.blackbookRunnersBase,
  blackbookRunners: (account: number | null) => [LocalConstants.blackbookRunnersBase, account],
  blackbookRunnersForJockey: (jockeyName: string) => ['blackbook-jockey-rides', jockeyName],

  eventDetails: 'event-details',

  countryEntitlementCheck: 'country-entitlement-check',

  feedBackContacts: 'contacts',
  feedBackSubjects: 'subjects',

  getBankAccount: 'getBankAccount',
  inducement: 'inducement',
  meetingPreview: 'meeting-preview',
  meetings: (date: string) => `meetings-${date}`,
  raceList: (meeting: string, date: string) => [meeting.toLowerCase(), date],

  mysteryBetDetails: 'mystery-bet-details',
  mysteryQuickPickPropose: 'mystery-quick-pick-propose',

  nextRaces: 'nextRaces',
  nextSkyRaces: 'next-sky-races',

  notifications: 'notifications',

  get pendingTicketsCountBase() {
    return [LocalConstants.accountBase, 'pending-bet-count']
  },
  get pendingTicketsCountKambi() {
    return [...this.pendingTicketsCountBase, 'kambi']
  },
  get pendingTicketsCountRacing() {
    return [...this.pendingTicketsCountBase, 'racing']
  },

  racePendingTickets: (raceKey: string) => [LocalConstants.accountBase, 'race-tickets', raceKey],
  raceResultedTickets: (
    meetingId: string,
    meetingDate: string,
    raceNumber: string,
    hasFixedResulted: boolean
  ) => [
    LocalConstants.accountBase,
    'race-tickets',
    meetingId,
    meetingDate,
    raceNumber,
    hasFixedResulted,
  ],

  // Used in RaceCard to invalidate cache due to legacy implementation
  racePageCompleteBase: LocalConstants.racePageCompleteBase,
  racePageFormBase: LocalConstants.racePageFormBase,
  racePageStartersForRaces: 'race-page-starters-for-races',

  racePageForm: (meetingId: string, meetingDate: Date | string, raceNumber: number) => [
    LocalConstants.racePageFormBase,
    meetingId.toLowerCase(),
    dayjs(meetingDate).format('YYYY-MM-DD'),
    raceNumber,
  ],
  racePageFieldSummary: (
    racecourseSeq: number | undefined,
    meetingDate: string | undefined,
    raceNumber: number | undefined
  ) => [
    LocalConstants.racePageFormBase,
    racecourseSeq,
    dayjs(meetingDate).format('YYYY-MM-DD'),
    raceNumber,
    'racePageFieldSummary',
  ],
  racePageFieldFOOForm: (
    racecourseSeq: number | undefined,
    meetingDate: string | undefined,
    raceNumber: number | undefined
  ) => [
    LocalConstants.racePageFormBase,
    racecourseSeq,
    dayjs(meetingDate).format('YYYY-MM-DD'),
    raceNumber,
    'racePageFieldFOOForm',
  ],
  racePageComplete: (meetingId: string, meetingDate: Date | string, raceNumber: number) => [
    LocalConstants.racePageCompleteBase,
    meetingId.toLowerCase(),
    dayjs(meetingDate).format('YYYY-MM-DD'),
    raceNumber,
  ],
  videoEntitlementRequest: (videoKey: string) => [LocalConstants.raceReplyBase, videoKey],
  raceReplayRequest: (meetingId: string, meetingDate: Date | string, raceNumber: number) => [
    LocalConstants.raceReplyBase,
    meetingId.toLowerCase(),
    dayjs(meetingDate).format('YYYY-MM-DD'),
    raceNumber,
  ],

  sameRaceMultiGetSelectionPrice: 'sameRaceMultiGetSelectionPrice',
  specials: 'specials',
  specialsTerms: 'specials-terms',
  takeABreak: 'take-a-break',

  serverConfig: 'mobi-server-config',

  toteSportsCurrentPools: 'tote-sports-current-pools',
  toteSportsPool: 'tote-sports-pool',
  toteSportsResultedPools: 'tote-sports-resulted-pools',

  trendingBets: 'trendingBets',

  userContactDetails: 'user-contact-details',

  betLoopSharedBetsFeedBase: (
    feedType?: BetLoopFeedParams['feedType'],
    type?: BetLoopFeedParams['type']
  ): string[] => {
    const feedBase = [LocalConstants.accountBase, LocalConstants.betLoopBase, 'feed']
    if (!feedType) return feedBase
    return [...feedBase, feedType, type ?? 'all']
  },

  get betLoopAccountProfileBase() {
    return [LocalConstants.accountBase, LocalConstants.betLoopBase, 'profile']
  },

  get betLoopAccountProfileFollowers() {
    return [...this.betLoopAccountProfileBase, 'followers-list']
  },

  get betLoopAccountProfileFollowing() {
    return [...this.betLoopAccountProfileBase, 'following-list']
  },

  get betLoopAccountExploreProfiles() {
    return [...this.betLoopAccountProfileBase, 'explore']
  },

  get betLoopAccountProfileStats() {
    return [...this.betLoopAccountProfileBase, 'stats']
  },

  betLoopSharePendingTickets: [
    LocalConstants.accountActivityBase,
    LocalConstants.betLoopBase,
    'share-pending-tickets',
  ],

  venueModeRacingRacingEvents: 'venueModeRacingRacingEvents',
} as const
